import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #shouldShowUnavailableProducts = false;

  /** @type {number | undefined} */
  #availabilitySyncTimer;

  /**
   * Track if final page reached
   */
  allPagesLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');

    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#syncAvailabilityFilterState();
    this.#scheduleAvailabilityFilterSync();
    Promise.all([
      this.#fetchPage('next'),
      this.#fetchPage('previous'),
    ]).then(() => {
      this.#completePaginationIfFullyLoaded();
    });
    this.#observeViewMore();
    requestAnimationFrame(() => {
      this.#completePaginationIfFullyLoaded();
    });

    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleAvailabilityFilterSync();
    requestAnimationFrame(() => {
      this.#completePaginationIfFullyLoaded();
    });
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage) return false;

    return (
      pageInfo.page >= 1 &&
      pageInfo.page <= Number(lastPage)
    );
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);

    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);

      newUrl.searchParams.set(
        'page',
        pageNumber.toString()
      );

      newUrl.hash = '';

      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageContent =
      await sectionRenderer.getSectionHTML(
        this.sectionId,
        true,
        pageInfo.url
      );

    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {number} page
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(
      pageHTML,
      'text/html'
    );

    const gridElement =
      parsedPage.querySelector('[ref="grid"]');

    if (!gridElement) return;

    return Array.from(
      gridElement.querySelectorAll(
        ':scope > [ref="cards[]"]'
      )
    );
  }

  /**
   * Reveal unavailable products after all pages loaded
   */
  #showUnavailableProducts() {
    const { grid } = this.refs;

    if (!grid) return;

    this.#sortUnavailableToBottom();

    grid.classList.add('product-grid--show-unavailable');
  }

  /**
   * When every product for this view is in the grid (no further "next" page),
   * reveal out-of-stock items at the bottom of the list.
   */
  #completePaginationIfFullyLoaded() {
    if (this.allPagesLoaded) return;

    const nextPage = this.#getPage('next');

    if (nextPage && this.#shouldUsePage(nextPage)) return;

    const { grid } = this.refs;

    if (!grid?.querySelector('.product-grid__item--unavailable')) return;

    this.allPagesLoaded = true;
    this.#showUnavailableProducts();
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    /**
     * FINAL PAGE REACHED
     */
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#completePaginationIfFullyLoaded();

      return;
    }

    let nextPageItemElements =
      this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;

      nextPageItemElements =
        this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState(
      '',
      '',
      nextPage.url.toString()
    );

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage =
      this.#getPage('previous');

    if (
      !previousPage ||
      !this.#shouldUsePage(previousPage)
    ) {
      return;
    }

    let previousPageItemElements =
      this.#getGridForPage(previousPage.page);

    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;

      previousPageItemElements =
        this.#getGridForPage(previousPage.page);

      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;

    const firstElement = grid.firstElementChild;

    const oldHeight = firstElement
      ? firstElement.getBoundingClientRect().top +
        window.scrollY
      : 0;

    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState(
      '',
      '',
      previousPage.url.toString()
    );

    if (firstElement) {
      const newHeight =
        firstElement.getBoundingClientRect().top +
        window.scrollY;

      const heightDiff = newHeight - oldHeight;

      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard =
      cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(
      targetCard.dataset.page
    );

    const page = isPrevious
      ? currentCardPage - 1
      : currentCardPage + 1;

    const url = new URL(window.location.href);

    url.searchParams.set(
      'page',
      page.toString()
    );

    url.hash = '';

    return {
      page,
      url,
    };
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error(
        'The section-id attribute is required'
      );
    }

    return id;
  }

  #applyAvailabilityFilterState() {
    const { grid } = this.refs;

    if (!grid) return;

    grid.classList.toggle(
      'product-grid--show-unavailable',
      this.#shouldShowUnavailableProducts || this.allPagesLoaded
    );
  }

  #scheduleAvailabilityFilterSync() {
    if (this.#availabilitySyncTimer) {
      window.clearTimeout(this.#availabilitySyncTimer);
    }

    this.#availabilitySyncTimer = window.setTimeout(() => {
      this.#syncAvailabilityFilterState();
      this.#applyAvailabilityFilterState();
    }, 0);

    this.#availabilitySyncTimer = window.setTimeout(() => {
      this.#syncAvailabilityFilterState();
      this.#applyAvailabilityFilterState();
    }, 1000);
  }

  // @ts-ignore
  #syncAvailabilityFilterState(queryParams) {
    const params =
      queryParams ??
      new URLSearchParams(window.location.search);

    this.#shouldShowUnavailableProducts =
      params.get('filter.v.availability') === '0';

    this.#applyAvailabilityFilterState();
  }

  #handleFilterUpdate = /** @param {Event & { detail?: { queryParams?: URLSearchParams } }} event */ (event) => {
    const { grid } = this.refs;

    this.pages.clear();

    this.allPagesLoaded = false;

    this.#syncAvailabilityFilterState(
      event?.detail?.queryParams
    );
    this.#scheduleAvailabilityFilterSync();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage =
      grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage =
        this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();

        this.#fetchPage('next');
      }
    });

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 3000);
    }
  };

  #sortUnavailableToBottom() {
  const { grid } = this.refs;

  if (!grid) return;

  const items = Array.from(
    grid.querySelectorAll(':scope > [ref="cards[]"]')
  );

  const available = [];
  const unavailable = [];

  for (const item of items) {
    if (item.getAttribute('data-available') === 'true') {
      available.push(item);
    } else {
      unavailable.push(item);
    }
  }

  const fragment = document.createDocumentFragment();

  for (const item of available) {
    fragment.appendChild(item);
  }

  for (const item of unavailable) {
    fragment.appendChild(item);
  }

  grid.replaceChildren(fragment);
}
}